import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CCardFooter,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionButton,
  CAccordionBody,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CInputGroup,
} from "@coreui/react-pro";
import {
  exportQuoteRequests,
  getQuoteRequests,
  updateQuoteRequest,
} from "../../utils/backend";

import { CPagination, CPaginationItem } from "@coreui/react-pro";

import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

const hasPermission = (permissionGroup, permissionName) => {
  let permissions;
  try {
    permissions = JSON.parse(localStorage.getItem("permissions")) || [];
  } catch (error) {
    console.error("Error parsing permissions from localStorage:", error);
    permissions = [];
  }

  return permissions.some(
    (perm) =>
      perm.permission_group === permissionGroup &&
      perm.permission_name === permissionName
  );
};

// The on-screen filters as the backend names them. The list and the Excel export both send this,
// so a download always contains exactly the requests the admin is looking at.
const toRequestFilters = (filters) => ({
  order_status: filters.orderStatus,
  veh_reg_no: filters.vehicleReg,
  policy_holder_name: filters.ownerName,
  id_type: filters.identityType,
  id_no: filters.idNumber,
  ownership_type: filters.type,
  company_name: filters.companyName,
  quote_preferred_insurers: filters.selectedInsuranceMultiple,
  insurance_plan: filters.selectedInsurance,
  qr_start_date: filters.qrStartDate,
  qr_end_date: filters.qrEndDate,
  qr_update_start_date: filters.qrUpdateStartDate,
  qr_update_end_date: filters.qrUpdateEndDate,
});

const Requests = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    orderStatus: "",
    vehicleReg: "",
    identityType: "",
    idNumber: "",
    ownerName: "",
    companyName: "",
    selectedInsuranceMultiple: "",
    selectedInsurance: "",
    qrUpdateStartDate: "",
    qrUpdateEndDate: "",
    qrStartDate: "",
    qrEndDate: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const savedState = JSON.parse(localStorage.getItem("requestsState"));
    if (savedState) {
      setFilters(savedState.filters || {});
      setPerPage(savedState.perPage || 10);
      setTotalItems(savedState.totalItems || 0);
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // rows and nothing on screen explained why.
      fetchData(
        savedState.filters || {},
        1,
        savedState.perPage || 10
      );
    } else {
      // Use default values for the first-time load
      const defaultFilters = {
        orderStatus: "",
        vehicleReg: "",
        identityType: "",
        idNumber: "",
        type: "",
        ownerName: "",
        companyName: "",
        selectedInsuranceMultiple: "",
        selectedInsurance: "",
        qrStartDate: "",
        qrEndDate: "",
        qrUpdateStartDate: "",
        qrUpdateEndDate: "",
      };
      fetchData(defaultFilters, 1, 10);
    }
  }, []);

  const checkForUpdates = async () => {
    try {
      fetchData(filters, currentPage, perPage);
    } catch (error) {
      console.error("Error checking for updates:", error);
    }
  };

  useEffect(() => {
    const interval = setInterval(checkForUpdates, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async (filters, page, perPage) => {
    //setIsLoading(isLoading);
    try {
      const response = await getQuoteRequests({
        ...toRequestFilters(filters),
        page,
        perPage,
      });

      if (response.success) {
        const mappedData = response.data.data.map((item) => {
          const orderDetails = item.order_details || {};
          const userDetails = item.user_details || {};

          return {
            id: item.id,
            type: item.ownership_type === "I" ? "Private" : "Company",
            vehicleReg: item.veh_reg_no || "N/A",
            identityType: item.id_type || "N/A",
            idNumber: item.id_no || "N/A",
            ownerName:
              item.policy_holder_name || userDetails.full_name || "N/A",
            postcode: item.postcode || orderDetails.postcode || "N/A",
            businessRegId: item.order_number || "N/A",
            companyName:
              item.company_name || orderDetails.company_name || "N/A",
            selectedInsurance: item.insurance_plan || "N/A",
            status: item.order_status || "N/A",
            createdAt: formatDateTime(item.created_at),
            order_id: item.order_id,
            selectedInsuranceMultiple: item.quote_preferred_insurers || "N/A",
            qrUpdateStartDate: (
              <div>
                {item.full_name},{formatDateTime(item.updated_at)}
              </div>
            ),
          };
        });

        setRequests(setUppercase(mappedData));
        setCurrentPage(response.data.currentPage);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
      } else {
        toast.error("Failed to fetch data");
        setRequests([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("An error occurred while fetching data");
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveStateToLocalStorage = (filters, perPage) => {
    localStorage.setItem(
      "requestsState",
      JSON.stringify({ filters, perPage })
    );
  };

  const handleSearch = (e) => {
    saveStateToLocalStorage(filters, perPage);
    fetchData(filters, 1, perPage);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    filters.orderStatus,
    filters.vehicleReg,
    filters.ownerName,
    filters.identityType,
    filters.idNumber,
    filters.type,
    filters.companyName,
    filters.selectedInsuranceMultiple,
    filters.selectedInsurance,
    filters.qrStartDate,
    filters.qrEndDate,
    filters.qrUpdateStartDate,
    filters.qrUpdateEndDate,
    perPage,
  ]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportQuoteRequests(toRequestFilters(filters));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quote_Requests_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    const resetFilters = {
      orderStatus: "",
      vehicleReg: "",
      identityType: "",
      idNumber: "",
      ownerName: "",
      companyName: "",
      type: "", // Ensure the 'type' key is included and reset properly
      selectedInsuranceMultiple: "",
      selectedInsurance: "",
      qrUpdateStartDate: "",
      qrUpdateEndDate: "",
      qrStartDate: "",
      qrEndDate: "",
    };
    setFilters(resetFilters);
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState =
      JSON.parse(localStorage.getItem("requestsState")) || {};
    localStorage.setItem(
      "requestsState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    // Pass reset filters, page, and perPage explicitly
    fetchData(resetFilters, 1, existingState.perPage || 10);
  };

  const handlePageChange = (page) => {
    saveStateToLocalStorage(filters, perPage);
    fetchData(filters, page, perPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    saveStateToLocalStorage(filters, newPerPage);
    fetchData(filters, 1, newPerPage);
  };

  const handleSort = (key) => {
    if (key === "id") {
      return;
    }
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc"; // Toggle back to ascending
    }
    setSortConfig({ key, direction });
    setRequests((prevRequests) => [...prevRequests]); // Trigger re-render to apply styles
  };

  const sortedRequests = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return requests;
    return [...requests].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [requests, sortConfig]);

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  // Surfaced next to Reset so an admin can never be silently filtered down to a subset.
  const activeFilterCount = [
    filters.orderStatus,
    filters.vehicleReg,
    filters.ownerName,
    filters.identityType,
    filters.idNumber,
    filters.type,
    filters.companyName,
    filters.selectedInsuranceMultiple,
    filters.selectedInsurance,
    filters.qrStartDate,
    filters.qrEndDate,
    filters.qrUpdateStartDate,
    filters.qrUpdateEndDate,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <>
              <CRow>
                <CCol className="justify-content-end d-flex my-1 align-items-center">
                  {activeFilterCount > 0 && (
                    <span
                      className="me-3 text-nowrap"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffe69c",
                        borderRadius: "12px",
                        color: "#664d03",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        padding: "4px 10px",
                      }}
                    >
                      <i className="bi bi-funnel-fill me-1"></i>
                      {activeFilterCount}{" "}
                      {activeFilterCount === 1 ? "filter" : "filters"} active —
                      showing a subset
                    </span>
                  )}
                  {/* Gated on what /admin/quote-request/export enforces (Order Management-List). */}
                  {hasPermission("Order Management", "List") && (
                    <CButton
                      color="success"
                      className="text-white me-2"
                      type="button"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <CSpinner size="sm" />
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-excel me-2"></i>
                          Download Excel
                        </>
                      )}
                    </CButton>
                  )}
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              <div style={{ overflowX: "auto" }}>
                <CTable borderless striped>
                  <CTableHead>
                    <CTableRow>
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell>
                        <CFormSelect
                          id="orderStatus"
                          value={filters.orderStatus}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              orderStatus: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Status</option>
                          <option value="Pending Quotation">
                            Pending Quotation
                          </option>
                          <option value="Quotation is Ready">
                            Quotation is Ready
                          </option>
                          <option value="Closed">Closed</option>
                        </CFormSelect>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.vehicleReg}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              vehicleReg: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.ownerName}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              ownerName: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.companyName}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              companyName: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.idNumber}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              idNumber: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.identityType}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              identityType: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.ownership_type}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              ownership_type: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.selectedInsuranceMultiple}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              selectedInsuranceMultiple: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={filters.selectedInsurance}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              selectedInsurance: e.target.value,
                            })
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CInputGroup>
                          <CFormInput
                            type="date"
                            value={filters.qrStartDate}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                qrStartDate: e.target.value,
                              })
                            }
                          />
                          <span className="input-group-text bg-light text-muted small">
                            to
                          </span>
                          <CFormInput
                            type="date"
                            value={filters.qrEndDate}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                qrEndDate: e.target.value,
                              })
                            }
                          />
                        </CInputGroup>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CInputGroup>
                          <CFormInput
                            type="date"
                            value={filters.qrUpdateStartDate}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                qrUpdateStartDate: e.target.value,
                              })
                            }
                          />
                          <span className="input-group-text bg-light text-muted small">
                            to
                          </span>
                          <CFormInput
                            type="date"
                            value={filters.qrUpdateEndDate}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                qrUpdateEndDate: e.target.value,
                              })
                            }
                          />
                        </CInputGroup>
                      </CTableDataCell>
                    </CTableRow>
                    <CTableRow>
                      {[
                        { key: "id", label: "ID." },
                        { key: "action", label: "Actions" },
                        { key: "status", label: "Request Status" },
                        { key: "vehicleReg", label: "Vehicle Number" },
                        { key: "ownerName", label: "Owner Name" },
                        { key: "companyName", label: "Company Name" },
                        { key: "idNumber", label: "ID Number" },
                        { key: "identityType", label: "ID Type" },
                        { key: "type", label: "Type" },
                        {
                          key: "selectedInsurance",
                          label: "Selected Insurance by customer (Multiple)",
                        },
                        {
                          key: "insurancePlan",
                          label: "User Purchase Insurance",
                        },
                        { key: "createdAt", label: "Created At" },
                        { key: "qrUpdateStartDate", label: "Updated By" },
                      ].map((column) => (
                        <CTableHeaderCell
                          key={column.key}
                          onClick={() => handleSort(column.key)}
                          className={`hover-header ${
                            sortConfig.key === column.key
                              ? "ctable-active-header"
                              : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            whiteSpace: "nowrap",
                            minWidth:
                              column.key === "createdAt" ||
                              column.key === "qrUpdateStartDate"
                                ? "150px"
                                : "auto",
                          }}
                        >
                          {column.label}
                        </CTableHeaderCell>
                      ))}
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {sortedRequests.map((request, index) => (
                      <CTableRow key={request.id}>
                        <CTableDataCell>
                          {index + 1 + perPage * (currentPage - 1)}
                        </CTableDataCell>
                        <CTableDataCell>
                          <div className="d-flex align-items-center gap-2">
                            {(request.status === "Quotation is Ready" ||
                              request.status === "Closed") &&
                              hasPermission("Quote Management", "View") && (
                                <CButton
                                  className="btn btn-sm btn-info text-white"
                                  title="Details"
                                  onClick={() =>
                                    navigate(`/requests/details/${request.id}`)
                                  }
                                >
                                  <i className="bi bi-eye-fill"></i>
                                </CButton>
                              )}
                            {request.status !== "Closed" &&
                              hasPermission("Quote Management", "Create") && ( // Check for permission
                                <CButton
                                  className="btn btn-sm btn-warning text-white"
                                  title="Upload Quotation"
                                  onClick={() =>
                                    navigate(
                                      `/requests/upload-quotation/${request.id}`
                                    )
                                  }
                                >
                                  <i className="bi bi-upload"></i>
                                </CButton>
                              )}
                          </div>
                        </CTableDataCell>
                        <CTableDataCell>
                          <span
                            style={{
                              backgroundColor:
                                request.status === "Pending Quotation"
                                  ? "#A9A9A9" // Orange
                                  : request.status === "Quotation is Ready"
                                  ? "#66BB6A" // Green
                                  : request.status === "Closed"
                                  ? "#000000" // Black
                                  : "#FFFFFF", // Default
                              color: "#fff",
                              border: "none",
                              width: "150px",
                              borderRadius: "20px",
                              fontWeight: "500",
                              fontSize: "14px",
                              display: "inline-block",
                              textAlign: "center",
                              padding: "10px 0px",
                            }}
                          >
                            {request.status.toUpperCase() || "N/A"}
                          </span>
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.vehicleReg || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.ownerName || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.companyName || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.idNumber || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.identityType || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>{request.type || "N/A"}</CTableDataCell>
                        <CTableDataCell>
                          {Array.isArray(request.selectedInsuranceMultiple) &&
                          request.selectedInsuranceMultiple.length > 0
                            ? request.selectedInsuranceMultiple.map(
                                (item, index) => (
                                  <div key={index}>{item.insurer_name}</div>
                                )
                              )
                            : "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.selectedInsurance || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.createdAt || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {request.qrUpdateStartDate || "N/A"}
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </div>
            </>
          )}
          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData(filters, page, perPage);
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 || // First page
                      pageNumber === totalPages - 1 || // Last page
                      pageNumber < 5 || // First few pages
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2) // Pages around the current page
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) || // Ellipsis after the first few pages
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4) // Ellipsis before the last page
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {requests.length} of {totalItems} requests
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
    </>
  );
};

export default Requests;
